import bootstrapBreakpoints from '../../utility/grid';
import { querySelector, KeyCodes, querySelectorAll } from '../../utility/utils';
import Scrolling from './scrolling';
import { offVPort, onVPort } from '../../utility/breakPointEvents';

import { BaseElements, BaseObject, BaseService, BaseInjector } from '../../js/base';
import HeaderItemsCloner from './header-items-cloner';
import QuickSearch from '../../widgets/quick-search';

class Texts extends BaseObject { }

class ClassList extends BaseObject {
    NAV_FIXED = 'navbar-light';
    NAV_NON_FIXED = 'navbar-dark';
    NAV_LIGHT = 'navbar-light';
    NAV_DARK = 'navbar-dark';
    HEADER_HERO = 'main-header--hero';
    BG_LIGHT = 'bg-white';
    NAV_EXPANDED = 'navbar-expanded';
    FIXED = 'fixed';
    FIXED_VISIBLE = 'fixed--visible';
    FIXED_ANIMATED = 'fixed--animated';
    SEARCH_UP = 'search-move-up';
    SEARCH_ACTIVE = 'search-active';
    SEARCH_PRE_ACTIVE = 'search-pre-active';
    SEARCH_POST_ACTIVE = 'search-post-active';
    BEFORE_NAV_EXPANDED = 'before-navbar-expanded';
    HIDE_NAV_BEFORE_EXPANDED = 'hide-before-navbar-expanded';
}

class Selectors extends BaseObject {
    quickSearchTrigger = '.quick-search__trigger';
    quickSearchLinks = '.quick-search__links';
    quickSearchInput = '.quick-search__input';
    navBar = '.navbar-nav';
    navBarContainer = '.navbar';
    navBrand = '.navbar-brand';
    search = '.nav-item--quick-search';
    mainMenu = '.main-menu';
    DROPDOWN = '.navbar-nav .nav-item:not(.nav-item--quick-search) .dropdown';
    headerWithSideBarDropdown = '.header-with-sidebar .dropdown:not(.quick-search__dropdown), .secondary-menu__list .dropdown';
    DROPDOWN_TOGGLE = '[data-toggle="dropdown"]';
    adsWrappers = '.header-ads';
    headerWithSideBar = '.header-with-sidebar';
    headerSideMenu = "#header-side-menu";
    publicationMenu = '.publication__menu';
    sidebarToggle = '.navbar-toggler';
    quickSearchToggle = '.quick-search__toggle';
    collapsibleQuickSearch = ".header-with-sidebar__quick-search";
    headerQuickSearchAutoComplete = ".header-with-sidebar__quick-search .quick-search__input";
}

class Elements extends BaseElements<Selectors> {
    trigger: HTMLElement
    navBar: HTMLElement;
    mainMenu: HTMLElement;
    navBarContainer: HTMLElement;
    navBrand: HTMLElement;
    search: HTMLElement;
    quickSearchInput: HTMLElement;
    wrapper: HTMLElement;
    headerWithSideBar: HTMLElement;
    headerSideMenu: HTMLElement;
    publicationMenu: HTMLElement;
    adsWrappers: HTMLElement;
    sidebarToggle: HTMLElement;
    quickSearchToggle: HTMLElement;
    headerQuickSearchAutoComplete: HTMLElement;

    constructor(readonly selectors: Selectors) {
        super(selectors, false);
    }

    setWrapper(wrapper: HTMLElement) {
        this.wrapper = wrapper;
        this.initElements();
    }

    initElements() {
        this.trigger = querySelector(this.selectors.quickSearchTrigger);
        this.navBar = querySelector(this.selectors.navBar, this.wrapper);
        this.mainMenu = querySelector(this.selectors.mainMenu, this.wrapper);
        this.navBarContainer = querySelector(this.selectors.navBarContainer, this.wrapper);
        this.navBrand = querySelector(this.selectors.navBrand, this.wrapper);
        this.search = null;
        this.quickSearchInput = querySelector(this.selectors.quickSearchInput, this.wrapper);
        this.headerWithSideBar = querySelector(this.selectors.headerWithSideBar);
        this.headerSideMenu = querySelector(this.selectors.headerSideMenu);
        this.publicationMenu = querySelector(this.selectors.publicationMenu);
        this.adsWrappers = querySelector(this.selectors.adsWrappers);
        this.sidebarToggle = querySelector(this.selectors.sidebarToggle);
        this.quickSearchToggle = querySelector(this.selectors.quickSearchToggle);
        this.headerQuickSearchAutoComplete = querySelector(this.selectors.headerQuickSearchAutoComplete);
    }

}

interface Header extends BaseService<Selectors, Elements, ClassList, Texts> { }

@BaseInjector(Selectors, Elements, ClassList, Texts)
class Header {
    protected _animationDuration: number;
    protected enableUpwardsFixedHeader: boolean;
    protected quickSearchLinksHandler: (Event) => void;
    protected _quickSearchLinks: JQuery;

    get animationDuration(): number {
        return 500;
    }

    constructor(readonly wrapper: HTMLElement, options = { fixHeaderOnScrollUp: true }) {
        this._animationDuration = this.animationDuration;
        this.enableUpwardsFixedHeader = options.fixHeaderOnScrollUp;
        if (wrapper.dataset.enableScrolling) {
            new Scrolling().initialize();
        }
        if (wrapper.dataset.enableCloning) {
            new HeaderItemsCloner(wrapper).initialize();
        }
    }

    initialize() {
        this.setElements();
        this.elements.quickSearchToggle?.addEventListener('click', () => {
            this.keydownClose(
                this.elements.sidebarToggle
            );
        });
        if(this.elements.quickSearchToggle) {
            this.elements.sidebarToggle?.addEventListener('click', () => {
                this.keydownClose(
                    this.elements.quickSearchToggle
                );
            });
        }
        if (this.elements?.navBar && this.elements?.navBarContainer) {
            this.manageMenuSearchRelation();
        }

        if (this.elements?.mainMenu) {
            this.responsiveMenuControl();
        }

        let dropdowns = this.base.convertToArray(querySelectorAll(this.selectors.DROPDOWN));
        // add the alt header with sidebar and the secondary menu dropdowns
        dropdowns = [...dropdowns, ...this.base.convertToArray(querySelectorAll(this.selectors.headerWithSideBarDropdown))]
        if (dropdowns.length) {
            if (window.innerWidth > bootstrapBreakpoints.xl) {
                this.initDropdowns(dropdowns);
            }
            onVPort('xl', (e) => {
                this.destroyDropdowns(dropdowns)
            });
            offVPort('xl', (e) => {
                this.initDropdowns(dropdowns);
            });
        }

        if (this.elements.headerWithSideBar) {
            this.setSidebarHeight();
        }

        if (this.elements.quickSearchInput && this._quickSearchLinks) {
            if (window.innerWidth < bootstrapBreakpoints.xl) {
                this.initQuickSearchLinks();
            }
            offVPort('xl', (e) => {
                this.destroyQuickSearchLinks();
            })
            onVPort('xl', (e) => {
                this.initQuickSearchLinks();
            })
        }
        this.focusOnCollapse(this.selectors.collapsibleQuickSearch, this.elements.headerQuickSearchAutoComplete);
        this.focusOnCollapse(this.selectors.headerSideMenu);
    }

    setElements(): void {
        this.elements.setWrapper(this.wrapper);
        this._quickSearchLinks = $(this.selectors.quickSearchLinks);


        if (this.elements.navBar) {
            this.elements.search = this.elements.navBar.querySelector(this.selectors.search);
        }
    }


    responsiveMenuControl(): void {
        const fixedHeaderControl = this.enableUpwardsFixedHeader ? this.upwardsFixedHeader.bind(this) : null;
        const $mainMenu = $(this.elements.mainMenu);
        if (fixedHeaderControl) {
            document.addEventListener('scroll', fixedHeaderControl, { passive: true });
            fixedHeaderControl()
        }

        onVPort('xl', (e) => {
            this.removeFixedControl(); // has to be earlier than heroOnViewportChange or anything that operates with navbar classes on viewport change
            window.removeEventListener('scroll', fixedHeaderControl);
            $mainMenu.on('show.bs.collapse hidden.bs.collapse', e => {
                if ($mainMenu[0] === e.target) {
                    this.responsiveLogoSwitch(e);
                    const action = e.type === 'show' ? 'add' : 'remove';
                    this.elements.navBarContainer.classList[action](this.classList.NAV_EXPANDED);
                }
            });
            if (this.elements.search?.classList.contains('search-active')) {
                this.elements.trigger.click()
            }
            this.heroOnViewportChange(e.type);
        })

        offVPort('xl', (e) => {
            window.addEventListener('scroll', fixedHeaderControl, { passive: true });
            $mainMenu.one('hide', () => {
                $mainMenu.off('show.bs.collapse hidden.bs.collapse hide.bs.collapse');
            });
            $mainMenu.collapse('hide');
            this.heroOnViewportChange(e.type);
        })
    }

    responsiveLogoSwitch(collapseEvent): void {
        const { navBrand } = this.elements;
        if (!navBrand) {
            return;
        }
        const onTransitionEnd = e => {
            if (e.target === e.currentTarget && e.propertyName === 'transform') {
                navBrand.classList[collapseEvent.type === 'show' ? 'add' : 'remove'](this.classList.BEFORE_NAV_EXPANDED);
                navBrand.classList.remove(this.classList.HIDE_NAV_BEFORE_EXPANDED);
                navBrand.removeEventListener('transitionend', onTransitionEnd);
            }
        };
        if (
            navBrand.classList.contains('switch-on-expand') &&
            (collapseEvent.type === 'show' || collapseEvent.type === 'hidden')
        ) {
            navBrand.addEventListener('transitionend', onTransitionEnd);
            navBrand.classList.add(this.classList.HIDE_NAV_BEFORE_EXPANDED);
        }
    }

    heroOnViewportChange(type: string): void {
        const { wrapper, navBarContainer } = this.elements;
        if (wrapper.classList.contains(this.classList.HEADER_HERO)) {
            if (type === 'screen-xl-on') {
                navBarContainer.classList.add(this.classList.NAV_LIGHT);
                navBarContainer.classList.remove(this.classList.NAV_DARK);
            } else {
                navBarContainer.classList.remove(this.classList.NAV_LIGHT);
                navBarContainer.classList.add(this.classList.NAV_DARK);
            }
        }
    }

    manageMenuSearchRelation(): void {
        const { wrapper, trigger, navBar, search, quickSearchInput } = this.elements;
        let expanded = false;
        const duration = this._animationDuration;

        const assignTransition = (element, which, menuChildrenCount, onFinish, initialDelay = null) => {
            setTimeout(() => {
                const nextTarget = element[`${which}ElementSibling`];
                if (nextTarget) {
                    assignTransition(nextTarget, which, menuChildrenCount, onFinish);
                } else if (typeof onFinish === 'function') {
                    element.addEventListener('transitionend', onFinish, { once: true });
                }
                if (!element.contains(trigger)) {
                    element.classList.toggle('search-move-up');
                }
            }, initialDelay || duration / menuChildrenCount);
        };

        if (wrapper && search && trigger && navBar && quickSearchInput) {
            trigger.addEventListener('click', (e) => {
                e.preventDefault();
                const which = expanded ? 'next' : 'previous';
                search.classList.toggle(this.classList.SEARCH_PRE_ACTIVE);
                setTimeout(() => {
                    assignTransition(
                        expanded ? navBar.firstElementChild : navBar.lastElementChild,
                        which,
                        navBar.children.length,
                        (e) => {
                            expanded = !expanded;
                            if (expanded) {
                                search.classList.add(this.classList.SEARCH_POST_ACTIVE);
                                quickSearchInput.focus();
                                trigger.setAttribute('aria-expanded', 'true');
                                trigger.setAttribute('aria-label', trigger.dataset?.collapseLabel);
                            } else {
                                search.classList.remove(this.classList.SEARCH_POST_ACTIVE);
                                trigger.setAttribute('aria-expanded', 'false');
                                trigger.setAttribute('aria-label', trigger.dataset?.expandLabel);
                            }
                        },
                        expanded ? duration * 0.6 : 0
                    );

                    setTimeout(() => {
                        search.classList.toggle(this.classList.SEARCH_ACTIVE);
                    }, expanded ? 0 : duration * 0.2);
                }, duration * 0.8);
            });
            document.addEventListener('keydown', this.handleEscOnSearch.bind(this));
        }
    }

    removeFixedControl(): void {
        const s = this.classList;
        const { wrapper, navBarContainer } = this.elements;
        wrapper.classList.remove(s.FIXED_ANIMATED, s.FIXED_VISIBLE, s.FIXED);
        wrapper.parentElement.style.paddingTop = '0px';
        navBarContainer.className = navBarContainer.dataset.preservedClassName;
    }

    upwardsFixedHeader(): Function {
        const { wrapper, navBarContainer } = this.elements;
        const removeFixedControl = this.removeFixedControl.bind(this);
        let prevScrollY = 0;
        const s = this.classList;
        navBarContainer.dataset.preservedClassName = navBarContainer.className;

        return function () {
            const { height } = wrapper.getBoundingClientRect();
            if (window.scrollY > height) {
                if (!wrapper.classList.contains(s.FIXED)) { // checking for class presence for the sake of performance
                    wrapper.classList.add(s.FIXED);
                    wrapper.parentElement.style.paddingTop = height + 'px';
                }
            } else {
                removeFixedControl();
                return;
            }

            if (prevScrollY > window.scrollY) {
                wrapper.classList.add(s.FIXED_ANIMATED, s.FIXED_VISIBLE);
                navBarContainer.classList.add(s.NAV_FIXED);
                navBarContainer.classList.remove(s.NAV_NON_FIXED);
            } else {
                wrapper.classList.remove(s.FIXED_VISIBLE);
            }
            prevScrollY = window.scrollY;
        };
    }

    initDropdowns(dropdowns: HTMLElement[]): void {
        dropdowns.forEach(dropdown => {
            dropdown.addEventListener('mouseenter', this.onDropdownOver.bind(this));
            dropdown.addEventListener('mouseleave', this.onDropdownOut.bind(this));
        });
    }

    destroyDropdowns(dropdowns: HTMLElement[]): void {
        dropdowns.forEach(dropdown => {
            dropdown.removeEventListener('mouseenter', this.onDropdownOver.bind(this));
            dropdown.removeEventListener('mouseleave', this.onDropdownOut.bind(this));
        });
    }

    initQuickSearchLinks(): void {
        this.quickSearchLinksHandler = this.showQuickSearchLinks.bind(this);
        this.elements.search?.addEventListener('click', this.quickSearchLinksHandler, true);
        this.elements.quickSearchInput.addEventListener('focus', this.quickSearchLinksHandler, true);
        this.hideQuickSearchLinks();
    }

    destroyQuickSearchLinks(): void {
        this.elements.search?.removeEventListener('click', this.quickSearchLinksHandler, true);
        this.elements.quickSearchInput.removeEventListener('focus', this.quickSearchLinksHandler, true);
        this.showQuickSearchLinks();
    }

    showQuickSearchLinks(): void {
        this._quickSearchLinks.collapse('show');
        this.elements.search?.removeEventListener('click', this.quickSearchLinksHandler);
        this.elements.quickSearchInput.removeEventListener('focus', this.quickSearchLinksHandler);
    }

    hideQuickSearchLinks(): void {
        this._quickSearchLinks.collapse('hide');
    }

    handleEscOnSearch(e: KeyboardEvent): void {
        const { trigger, search } = this.elements;
        if (e.keyCode === KeyCodes.ESC && search.classList.contains(this.classList.SEARCH_POST_ACTIVE) && search.classList.contains(this.classList.SEARCH_PRE_ACTIVE)) {
            trigger.click();
            trigger.focus();
        }
    }

    onDropdownOver(e: { target: HTMLElement; }): void {
        const toggler = e.target.children[0];
        $(toggler).dropdown('toggle').blur();
    }
    onDropdownOut(e: { target: HTMLElement; }): void {
        if (e.target.classList.contains('show')) {
            const toggler = e.target.children[0];
            $(toggler).dropdown('toggle');
        }
    }
    setSidebarHeight() {
        let adsWrappersHeight = this.elements.adsWrappers?.offsetHeight || 0;
        let headerHeight = this.elements.headerWithSideBar?.offsetHeight || 0;
        let totalSidebarHeight = adsWrappersHeight + headerHeight;
        this.elements.headerSideMenu.style.maxHeight = `calc(100% - ${totalSidebarHeight}px )`;
    }
    keydownClose(trigger: HTMLElement) {
        if (!trigger.classList.contains('collapsed')) {
            trigger.click();
        }
    }
    focusOnCollapse(containerSelector: string, element?: HTMLElement) {
        // add focus to the element after collapse
        const target: HTMLElement | ChildNode = element ? element : querySelector(containerSelector)?.firstChild;
        if (target) {
            $(containerSelector).on('shown.bs.collapse', e => {
                if(e.target == e.currentTarget){
                    if (target && target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) {
                        // to move the cursor to the end of an input or textarea
                        const end = target.value.length;
                        target.setSelectionRange(end, end);
                    }
                    (target as HTMLElement)?.focus();
                }
            });
        }
    }
}

export default Header;

export {
    Elements as HeaderElements,
    Selectors as HeaderSelectors,
    ClassList as HeaderClassList,
    Texts as HeaderTexts
}
